// DipField Suite project file, version 2. One object holds everything a project needs to
// reproduce its results: the data, the joint sets, every setting, and the versions of the
// engines that produced the saved results. Units are fixed (D-006): kN, kPa, m, degrees.
// docs/schema.md describes every field. GPL-3.0-or-later.
import type { OrientationFormat, Projection, SetWindow, Traverse, OverlayKind, RosetteMode, DseProjection } from "@dipfield/stereo";

export const PROJECT_FORMAT = "dipfield-suite/project";
export const PROJECT_VERSION = 2;

/** The units every number in the file is stated in. Written into each file so a reader
 *  never has to guess; there is no other choice. */
export const UNITS = { force: "kN", stress: "kPa", length: "m", angle: "deg", unitWeight: "kN/m3", density: "% per 1% area" } as const;
export type Units = typeof UNITS;

// ------------------------------------------------------------------ data
/** One measured discontinuity. dip, dipDir: degrees. quantity: count (> 0). */
export interface Plane { dip: number; dipDir: number; quantity: number; traverse?: string }
/** A sampling traverse for Terzaghi weighting (stereo TRZ-01). */
export interface TraverseDef { id: string; name: string; tr: Traverse }
export interface DataSection {
  /** How orientations are shown and typed (the data are always stored as dip / dip direction). */
  format: OrientationFormat;
  planes: Plane[];
  traverses: TraverseDef[];
  /** Terzaghi weighting: on/off and the minimum bias angle, degrees (DIPS default 15). */
  terzaghi: { use: boolean; minBiasDeg: number };
}

// ------------------------------------------------------------------ joint sets
export interface JointSet {
  id: string; name: string; window: SetWindow;
  /** Friction angle for kinematic screening, degrees; null = the slope's global value. */
  friction: number | null;
}

// ------------------------------------------------------------------ stereonet settings
export interface StereoSettings {
  projection: Projection;
  density: {
    method: "fisher-overlap" | "schmidt";
    /** Counting circle as a fraction of the hemisphere area (0.01 = 1%). */
    countCircle: number;
    coneRule: "dips" | "exact";
  };
  display: {
    view: "net" | "rose";
    poles: boolean; poleSize: number;
    contours: boolean; contourStyle: "bands" | "smooth";
    overlay: OverlayKind;
    globalMean: boolean; bestFit: boolean;
    setPlanes: boolean; cones: boolean;
  };
  rose: {
    mode: RosetteMode; bin: number; dipMin: number; dipMax: number;
    scale: "area" | "linear"; set: string; showMean: boolean;
    ring: "auto" | "fixed"; ringPct: number; overlay: boolean;
  };
  /** Automatic set identification (stereo DSE-01..05). */
  dse: { maxSets: number; minAngleDeg: number; coneDeg: number; minPeakPct: number; projection: DseProjection; useWeights: boolean };
}

// ------------------------------------------------------------------ slope (Phase 3)
/** Joint shear strength (packages/rock, strength.ts). c, jcs, pc, d, waterPressure: kPa.
 *  phi, phir, waviness: degrees. spacing: m (toppling block width). a, b: power curve. */
export interface Material {
  model: "mc" | "bb" | "power";
  c: number; phi: number;
  jrc: number; jcs: number; phir: number;
  a: number; b: number; pc: number; d: number;
  waviness: number; waterPressure: number; spacing: number;
}
export interface SlopeSection {
  /** The slope face, shared by kinematic screening and limit equilibrium. Degrees. */
  face: { dip: number; dipDir: number };
  /** Lateral limits ±, degrees. */
  lateral: number;
  kinematics: {
    enabled: boolean; friction: number; noLimits: boolean;
    show: "planar" | "wedge" | "flexural" | "direct"; pairs: "all" | "sets"; showZone: boolean;
    chart: "dipDir" | "dip" | "friction";
  };
  /** Limit-equilibrium geometry. upper: upper slope surface (degrees); height, length,
   *  bench: m; unitWeight: kN/m³. bench null = no bench. */
  geometry: { upper: { dip: number; dipDir: number }; height: number; length: number; unitWeight: number; bench: number | null };
  /** Strength per joint set id; `other` for poles outside every set. */
  materials: Record<string, Material>;
  other: Material;
  /** toppleBase: ψb, degrees (null = block bases + 5°); toppleToe: support at the toe, kN per metre;
   *  Toppling uses @dipfield/slope's contact rules; the `toppleRules` of files 2.1.0–2.5.0 is not
   *  read (2.6.0, D-025).
   *  basis: "sets" for a new project since 2.3.0 ("poles" before); with no joint sets the Suite uses
   *  every pole and every pole pair whatever basis and pairs hold (D-022). */
  run: { basis: "sets" | "poles"; pairs: "between" | "all"; planar: boolean; wedge: boolean; topple: boolean; target: number;
    toppleBase: number | null; toppleToe: number; toppleMethod: "equal-area" | "goodman-bray" };
  /** fill: % of the tension crack / joint height filled; seismic k: g; force: kN; trend, plunge: degrees. */
  loads: { water: "none" | "hb" | "custom"; fill: number; seismicK: number; seismicTrend: number | null; force: number; forceTrend: number; forcePlunge: number };
  /** Block size caps: volume m³, weight kN, trace m, persistence m. null = no cap. */
  caps: { volume: number | null; weight: number | null; trace: number | null; persistence: number | null };
  /** What the Slope module shows: the centre view, wedge intersections on the net, and the
   *  failure mode listed among the lowest factors of safety. */
  view: { tab: "net" | "3d" | "chart"; intersections: boolean; listKind: "all" | "planar" | "wedge" | "topple" };
}

// ------------------------------------------------------------------ tunnel (Phase 4)
/** Tunnel geometry for wedge failure-mode assessment (wedge3d, D-003: no support, no field
 *  stress). width, height, length: m (circle: width = diameter; length null = unbounded drive);
 *  trend, plunge: degrees (plunge positive down); unitWeight: kN/m³. The joints are the sets'
 *  mean planes with their strength (slope.materials, D-021); `joints` says which are used.
 *  section: the custom section's vertices (x right, z up, m), kept when a preset is chosen;
 *  sectionName: where it came from (a DXF file name, or null when drawn). combo: the joint
 *  combination on show, set ids joined by "," ("all" = every one; null = the critical one).
 *  target: design factor of safety. view: the centre tab and which wedges are listed. */
export interface TunnelSection {
  shape: "horseshoe" | "circle" | "rectangle" | "custom";
  width: number; height: number; section: Array<[number, number]> | null; sectionName: string | null;
  trend: number; plunge: number; length: number | null; unitWeight: number;
  joints: Record<string, { use: boolean }>;
  combo: string | null;
  target: number;
  view: { tab: "3d" | "section" | "net"; perimeter: boolean; nearEnd: boolean; farEnd: boolean; unstableOnly: boolean };
}

// ------------------------------------------------------------------ report (Phase 5)
/** The report (Phase 5): what it includes and the text of its cover. Results are computed
 *  afresh from the project when the report is shown, never stored (D-024). title: "" = the
 *  project name. parts: the four parts; data, slope, tunnel: the items within each part. */
export interface ReportSection {
  title: string; author: string; notes: string;
  parts: { data: boolean; slope: boolean; tunnel: boolean; method: boolean };
  data: { net: boolean; rosette: boolean; sets: boolean; poles: boolean };
  slope: { inputs: boolean; kinematics: boolean; blocks: boolean; charts: boolean; sheets: boolean };
  tunnel: { inputs: boolean; combos: boolean; figures: boolean; wedges: boolean; modes: boolean };
}

// ------------------------------------------------------------------ the file
export interface Migration { from: string; version: number; at: string; notes: string[] }
export interface Meta {
  id: string; name: string;
  createdAt: string; savedAt: string;
  /** The app that last saved the file, and the engine versions it ran (docs/ENGINE-GUIDE.md). */
  savedBy: { app: string; version: string } | null;
  engines: Record<string, string>;
  migratedFrom: Migration | null;
}
export interface Project {
  format: typeof PROJECT_FORMAT;
  version: typeof PROJECT_VERSION;
  meta: Meta;
  units: Units;
  data: DataSection;
  sets: JointSet[];
  stereo: StereoSettings;
  slope: SlopeSection;
  tunnel: TunnelSection;
  report: ReportSection;
}
